import React, { useState, useEffect, useMemo } from 'react';
import { Building2, TrendingUp, Users, Eye, RefreshCw, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const formatNum = (n) => {
  if (n == null || isNaN(n)) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const sourceColor = (source) => {
  const s = (source || '').toLowerCase();
  if (s === 'instagram') return '#E1306C';
  if (s === 'tiktok') return '#010101';
  if (s === 'youtube') return '#FF0000';
  if (s === 'facebook') return '#1877F2';
  if (s === 'linkedin') return '#0077B5';
  return 'var(--cyan)';
};

const SkeletonCard = () => (
  <div style={{
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 20,
    animation: 'pulse 1.5s ease-in-out infinite',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--bg3)' }} />
      <div style={{ flex: 1 }}>
        <div style={{ height: 14, background: 'var(--bg3)', borderRadius: 4, width: '60%', marginBottom: 6 }} />
        <div style={{ height: 11, background: 'var(--bg3)', borderRadius: 4, width: '35%' }} />
      </div>
    </div>
    <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
      <div style={{ height: 22, width: 72, background: 'var(--bg3)', borderRadius: 20 }} />
      <div style={{ height: 22, width: 72, background: 'var(--bg3)', borderRadius: 20 }} />
    </div>
    <div style={{ display: 'flex', gap: 12 }}>
      <div style={{ flex: 1, height: 48, background: 'var(--bg3)', borderRadius: 8 }} />
      <div style={{ flex: 1, height: 48, background: 'var(--bg3)', borderRadius: 8 }} />
      <div style={{ flex: 1, height: 48, background: 'var(--bg3)', borderRadius: 8 }} />
    </div>
  </div>
);

export default function Agenzia() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroSource, setFiltroSource] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get('/meta_analytics/agenzia');
        setData(res.data.agenzia || []);
      } catch (e) {
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const uniqueSources = useMemo(() => {
    const s = new Set(data.map((r) => r.source).filter(Boolean));
    return Array.from(s);
  }, [data]);

  const grouped = useMemo(() => {
    const map = {};
    data.forEach((row) => {
      if (!map[row.account_key]) {
        map[row.account_key] = {
          account_key: row.account_key,
          account_label: row.account_label,
          logo_url: row.logo_url,
          valore_contratto: row.valore_contratto,
          stato: row.stato,
          sources: [],
        };
      }
      map[row.account_key].sources.push(row);
    });
    return Object.values(map);
  }, [data]);

  const filtered = useMemo(() => {
    return grouped.filter((client) => {
      const matchSearch = !search || client.account_label?.toLowerCase().includes(search.toLowerCase());
      const matchSource = !filtroSource || client.sources.some((s) => s.source === filtroSource);
      return matchSearch && matchSource;
    }).map((client) => {
      if (filtroSource) {
        return { ...client, sources: client.sources.filter((s) => s.source === filtroSource) };
      }
      return client;
    });
  }, [grouped, search, filtroSource]);

  const summaryStats = useMemo(() => {
    const totalClients = grouped.length;
    let topClient = null;
    let topReach = -1;
    let totalFollowers = 0;
    let followerCount = 0;
    grouped.forEach((client) => {
      let clientReach = 0;
      client.sources.forEach((s) => {
        const r = s.metrics?.reach_1d || 0;
        clientReach += r;
        totalFollowers += s.metrics?.followers_count || 0;
        followerCount++;
      });
      if (clientReach > topReach) {
        topReach = clientReach;
        topClient = client;
      }
    });
    const avgFollowers = followerCount > 0 ? Math.round(totalFollowers / followerCount) : 0;
    return { totalClients, topClient, topReach, avgFollowers };
  }, [grouped]);

  return (
    <div className="eden-page">
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .agenzia-client-card:hover {
          border-color: var(--cyan-dim) !important;
          transform: translateY(-1px);
        }
        .source-chip-pill:hover {
          opacity: 0.85;
        }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <Building2 size={28} color="var(--cyan)" />
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>Vista Agenzia</h1>
      </div>
      <p style={{ margin: '0 0 24px 0', color: 'var(--text-muted)', fontSize: 14 }}>
        Performance aggregata di tutti i clienti
      </p>

      {!loading && data.length > 0 && (
        <div style={{
          display: 'flex',
          gap: 16,
          marginBottom: 24,
          flexWrap: 'wrap',
        }}>
          <div className="eden-card" style={{ flex: '1 1 160px', padding: '16px 20px', minWidth: 140 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Building2 size={16} color="var(--cyan)" />
              <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Clienti attivi</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)' }}>{summaryStats.totalClients}</div>
          </div>
          <div className="eden-card" style={{ flex: '1 1 200px', padding: '16px 20px', minWidth: 180 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <TrendingUp size={16} color="var(--green)" />
              <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Top per reach</span>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {summaryStats.topClient?.account_label || '—'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--green)' }}>{formatNum(summaryStats.topReach)} reach/1d</div>
          </div>
          <div className="eden-card" style={{ flex: '1 1 160px', padding: '16px 20px', minWidth: 140 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Users size={16} color="var(--yellow)" />
              <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Followers medi</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)' }}>{formatNum(summaryStats.avgFollowers)}</div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 320 }}>
          <Search size={16} color="var(--text-muted)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          <input
            className="eden-input"
            placeholder="Cerca cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 34, width: '100%', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            className="source-chip-pill"
            onClick={() => setFiltroSource('')}
            style={{
              padding: '6px 14px',
              borderRadius: 20,
              border: '1px solid',
              borderColor: filtroSource === '' ? 'var(--cyan)' : 'var(--border)',
              background: filtroSource === '' ? 'var(--cyan-dim)' : 'transparent',
              color: filtroSource === '' ? 'var(--cyan)' : 'var(--text-muted)',
              fontSize: 13,
              cursor: 'pointer',
              fontWeight: filtroSource === '' ? 600 : 400,
              transition: 'all 0.15s',
            }}
          >
            Tutti
          </button>
          {uniqueSources.map((src) => (
            <button
              key={src}
              className="source-chip-pill"
              onClick={() => setFiltroSource(src === filtroSource ? '' : src)}
              style={{
                padding: '6px 14px',
                borderRadius: 20,
                border: '1px solid',
                borderColor: filtroSource === src ? sourceColor(src) : 'var(--border)',
                background: filtroSource === src ? `${sourceColor(src)}22` : 'transparent',
                color: filtroSource === src ? sourceColor(src) : 'var(--text-muted)',
                fontSize: 13,
                cursor: 'pointer',
                fontWeight: filtroSource === src ? 600 : 400,
                textTransform: 'capitalize',
                transition: 'all 0.15s',
              }}
            >
              {src}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: 16,
        }}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 24px',
          color: 'var(--text-muted)',
          textAlign: 'center',
        }}>
          <Building2 size={48} color="var(--border)" style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-dim)', marginBottom: 8 }}>
            Nessun dato analytics disponibile.
          </div>
          <div style={{ fontSize: 14 }}>
            I dati appaiono dopo il primo ingestion.
          </div>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: 16,
        }}>
          {filtered.map((client) => {
            const totalFollowers = client.sources.reduce((acc, s) => acc + (s.metrics?.followers_count || 0), 0);
            const totalReach7d = client.sources.reduce((acc, s) => acc + (s.metrics?.reach_7d || 0), 0);
            const latestSnapshot = client.sources.reduce((latest, s) => {
              if (!latest) return s.snapshot_date;
              return s.snapshot_date > latest ? s.snapshot_date : latest;
            }, null);

            return (
              <div
                key={client.account_key}
                className="eden-card agenzia-client-card"
                onClick={() => navigate(`/analytics?account=${client.account_key}`)}
                style={{
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, transform 0.15s',
                  padding: 20,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  {client.logo_url ? (
                    <img
                      src={client.logo_url}
                      alt={client.account_label}
                      style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: 'var(--bg3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Building2 size={16} color="var(--text-muted)" />
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: 'var(--text)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {client.account_label}
                    </div>
                    {latestSnapshot && (
                      <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                        Aggiornato il {formatDate(latestSnapshot)}
                      </div>
                    )}
                  </div>
                  {client.valore_contratto && (
                    <div style={{
                      padding: '3px 8px',
                      borderRadius: 10,
                      background: 'var(--green-dim)',
                      color: 'var(--green)',
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}>
                      € {Number(client.valore_contratto).toLocaleString('it-IT')}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                  {client.sources.map((s) => (
                    <div
                      key={s.source}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '4px 10px',
                        borderRadius: 20,
                        border: `1px solid ${sourceColor(s.source)}44`,
                        background: `${sourceColor(s.source)}18`,
                      }}
                    >
                      <span style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: sourceColor(s.source),
                        flexShrink: 0,
                        display: 'inline-block',
                      }} />
                      <span style={{ fontSize: 12, color: sourceColor(s.source), fontWeight: 600, textTransform: 'capitalize' }}>
                        {s.source}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {formatNum(s.metrics?.reach_1d || 0)} reach
                      </span>
                    </div>
                  ))}
                </div>

                {client.sources.length > 1 ? (
                  <div>
                    {client.sources.map((s) => (
                      <div
                        key={s.source}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '7px 0',
                          borderTop: '1px solid var(--border)',
                        }}
                      >
                        <span style={{
                          fontSize: 12,
                          color: sourceColor(s.source),
                          textTransform: 'capitalize',
                          fontWeight: 600,
                          width: 76,
                          flexShrink: 0,
                        }}>
                          {s.source}
                        </span>
                        <div style={{ display: 'flex', gap: 16, flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Users size={12} color="var(--text-dim)" />
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatNum(s.metrics?.followers_count || 0)}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Eye size={12} color="var(--text-dim)" />
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatNum(s.metrics?.reach_7d || 0)}/7d</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{
                      flex: 1,
                      background: 'var(--bg2)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      textAlign: 'center',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
                        <Users size={13} color="var(--text-dim)" />
                        <span style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Followers</span>
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{formatNum(totalFollowers)}</div>
                    </div>
                    <div style={{
                      flex: 1,
                      background: 'var(--bg2)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      textAlign: 'center',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
                        <Eye size={13} color="var(--text-dim)" />
                        <span style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reach 7d</span>
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{formatNum(totalReach7d)}</div>
                    </div>
                    <div style={{
                      flex: 1,
                      background: 'var(--bg2)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      textAlign: 'center',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
                        <TrendingUp size={13} color="var(--text-dim)" />
                        <span style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reach 1d</span>
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
                        {formatNum(client.sources[0]?.metrics?.reach_1d || 0)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
